import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e8d702d2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "image-style-panel" }
const _hoisted_2 = /*#__PURE__*/_createTextVNode(" 裁剪图片")
const _hoisted_3 = { class: "clip" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "title" }, "按形状：", -1))
const _hoisted_5 = { class: "shape-clip" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  key: 0,
  class: "title"
}
const _hoisted_8 = /*#__PURE__*/_createTextVNode(" 替换图片")
const _hoisted_9 = /*#__PURE__*/_createTextVNode(" 重置样式")
const _hoisted_10 = /*#__PURE__*/_createTextVNode(" 设为背景")

import { Ref, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore } from '@/store'
import { PPTImageElement, SlideBackground } from '@/types/slides'
import { CLIPPATHS } from '@/configs/imageClip'
import { getImageDataURL } from '@/utils/image'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

import ElementOutline from '../common/ElementOutline.vue'
import ElementShadow from '../common/ElementShadow.vue'
import ElementFlip from '../common/ElementFlip.vue'
import ElementFilter from '../common/ElementFilter.vue'
import ElementColorMask from '../common/ElementColorMask.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageStylePanel',
  setup(__props) {

const shapeClipPathOptions = CLIPPATHS
const ratioClipOptions = [
  {
    label: '纵横比（方形）',
    children: [
      { key: '1:1', ratio: 1 / 1 },
    ],
  },
  {
    label: '纵横比（纵向）',
    children: [
      { key: '2:3', ratio: 3 / 2 },
      { key: '3:4', ratio: 4 / 3 },
      { key: '3:5', ratio: 5 / 3 },
      { key: '4:5', ratio: 5 / 4 },
    ],
  },
  {
    label: '纵横比（横向）',
    children: [
      { key: '3:2', ratio: 2 / 3 },
      { key: '4:3', ratio: 3 / 4 },
      { key: '5:3', ratio: 3 / 5 },
      { key: '5:4', ratio: 4 / 5 },
    ],
  },
  {
    children: [
      { key: '16:9', ratio: 9 / 16 },
      { key: '16:10', ratio: 10 / 16 },
    ],
  },
]

const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const { handleElement, handleElementId } = storeToRefs(mainStore)
const { currentSlide } = storeToRefs(slidesStore)

const handleImageElement = handleElement as Ref<PPTImageElement>

const clipPanelVisible = ref(false)

const { addHistorySnapshot } = useHistorySnapshot()

// 打开自由裁剪
const clipImage = () => {
  mainStore.setClipingImageElementId(handleElementId.value)
  clipPanelVisible.value = false
}

// 获取原始图片的位置大小
const getImageElementDataBeforeClip = () => {
  const _handleElement = handleElement.value as PPTImageElement

  // 图片当前的位置大小和裁剪范围
  const imgWidth = _handleElement.width
  const imgHeight = _handleElement.height
  const imgLeft = _handleElement.left
  const imgTop = _handleElement.top
  const originClipRange: [[number, number], [number, number]] = _handleElement.clip ? _handleElement.clip.range : [[0, 0], [100, 100]]

  const originWidth = imgWidth / ((originClipRange[1][0] - originClipRange[0][0]) / 100)
  const originHeight = imgHeight / ((originClipRange[1][1] - originClipRange[0][1]) / 100)
  const originLeft = imgLeft - originWidth * (originClipRange[0][0] / 100)
  const originTop = imgTop - originHeight * (originClipRange[0][1] / 100)

  return {
    originClipRange,
    originWidth,
    originHeight,
    originLeft,
    originTop,
  }
}

// 预设裁剪
const presetImageClip = (shape: string, ratio = 0) => {
  const _handleElement = handleElement.value as PPTImageElement

  const {
    originClipRange,
    originWidth,
    originHeight,
    originLeft,
    originTop,
  } = getImageElementDataBeforeClip()
  
  // 纵横比裁剪（形状固定为矩形）
  if (ratio) {
    const imageRatio = originHeight / originWidth

    const min = 0
    const max = 100
    let range: [[number, number], [number, number]]

    if (imageRatio > ratio) {
      const distance = ((1 - ratio / imageRatio) / 2) * 100
      range = [[min, distance], [max, max - distance]]
    }
    else {
      const distance = ((1 - imageRatio / ratio) / 2) * 100
      range = [[distance, min], [max - distance, max]]
    }
    slidesStore.updateElement({
      id: handleElementId.value,
      props: {
        clip: { ..._handleElement.clip, shape, range },
        left: originLeft + originWidth * (range[0][0] / 100),
        top: originTop + originHeight * (range[0][1] / 100),
        width: originWidth * (range[1][0] - range[0][0]) / 100,
        height: originHeight * (range[1][1] - range[0][1]) / 100,
      },
    })
  }
  // 形状裁剪（保持当前裁剪范围）
  else {
    slidesStore.updateElement({
      id: handleElementId.value,
      props: {
        clip: { ..._handleElement.clip, shape, range: originClipRange }
      },
    })
  }
  clipImage()
  addHistorySnapshot()
}

// 替换图片（保持当前的样式）
const replaceImage = (files: FileList) => {
  const imageFile = files[0]
  if (!imageFile) return
  getImageDataURL(imageFile).then(dataURL => {
    const props = { src: dataURL }
    slidesStore.updateElement({ id: handleElementId.value, props })
  })
  addHistorySnapshot()
}

// 重置图片：清除全部样式
const resetImage = () => {
  const _handleElement = handleElement.value as PPTImageElement

  if (_handleElement.clip) {
    const {
      originWidth,
      originHeight,
      originLeft,
      originTop,
    } = getImageElementDataBeforeClip()

    slidesStore.updateElement({
      id: handleElementId.value,
      props: {
        left: originLeft,
        top: originTop,
        width: originWidth,
        height: originHeight,
      },
    })
  }

  slidesStore.removeElementProps({
    id: handleElementId.value,
    propName: ['clip', 'outline', 'flip', 'shadow', 'filters', 'colorMask'],
  })
  addHistorySnapshot()
}

// 将图片设置为背景
const setBackgroundImage = () => {
  const _handleElement = handleElement.value as PPTImageElement

  const background: SlideBackground = {
    ...currentSlide.value.background,
    type: 'image',
    image: _handleElement.src,
    imageSize: 'cover',
  }
  slidesStore.updateSlide({ background })
  addHistorySnapshot()
}

return (_ctx: any,_cache: any) => {
  const _component_IconTailoring = _resolveComponent("IconTailoring")!
  const _component_Button = _resolveComponent("Button")!
  const _component_ButtonGroup = _resolveComponent("ButtonGroup")!
  const _component_IconDown = _resolveComponent("IconDown")!
  const _component_Popover = _resolveComponent("Popover")!
  const _component_Divider = _resolveComponent("Divider")!
  const _component_IconTransform = _resolveComponent("IconTransform")!
  const _component_FileInput = _resolveComponent("FileInput")!
  const _component_IconUndo = _resolveComponent("IconUndo")!
  const _component_IconTheme = _resolveComponent("IconTheme")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "origin-image",
      style: _normalizeStyle({ backgroundImage: `url(${_unref(handleImageElement).src})` })
    }, null, 4),
    _createVNode(ElementFlip),
    _createVNode(_component_ButtonGroup, { class: "row" }, {
      default: _withCtx(() => [
        _createVNode(_component_Button, {
          style: {"flex":"5"},
          onClick: _cache[0] || (_cache[0] = ($event: any) => (clipImage()))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_IconTailoring, { class: "btn-icon" }),
            _hoisted_2
          ]),
          _: 1
        }),
        _createVNode(_component_Popover, {
          trigger: "click",
          visible: clipPanelVisible.value,
          "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => ((clipPanelVisible).value = $event))
        }, {
          content: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _hoisted_4,
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(shapeClipPathOptions), (item, key) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "shape-clip-item",
                    key: key,
                    onClick: ($event: any) => (presetImageClip(key))
                  }, [
                    _createElementVNode("div", {
                      class: "shape",
                      style: _normalizeStyle({ clipPath: item.style })
                    }, null, 4)
                  ], 8, _hoisted_6))
                }), 128))
              ]),
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ratioClipOptions, (type) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: type.label
                }, [
                  (type.label)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, "按" + _toDisplayString(type.label) + "：", 1))
                    : _createCommentVNode("", true),
                  _createVNode(_component_ButtonGroup, { class: "row" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(type.children, (item) => {
                        return (_openBlock(), _createBlock(_component_Button, {
                          style: {"flex":"1"},
                          key: item.key,
                          onClick: ($event: any) => (presetImageClip('rect', item.ratio))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(item.key), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      }), 128))
                    ]),
                    _: 2
                  }, 1024)
                ], 64))
              }), 64))
            ])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_Button, {
              class: "no-padding",
              style: {"flex":"1"}
            }, {
              default: _withCtx(() => [
                _createVNode(_component_IconDown)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["visible"])
      ]),
      _: 1
    }),
    _createVNode(_component_Divider),
    _createVNode(ElementColorMask),
    _createVNode(_component_Divider),
    _createVNode(ElementFilter),
    _createVNode(_component_Divider),
    _createVNode(ElementOutline),
    _createVNode(_component_Divider),
    _createVNode(ElementShadow),
    _createVNode(_component_Divider),
    _createVNode(_component_FileInput, {
      onChange: _cache[2] || (_cache[2] = files => replaceImage(files))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_Button, { class: "full-width-btn" }, {
          default: _withCtx(() => [
            _createVNode(_component_IconTransform, { class: "btn-icon" }),
            _hoisted_8
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_Button, {
      class: "full-width-btn",
      onClick: _cache[3] || (_cache[3] = ($event: any) => (resetImage()))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_IconUndo, { class: "btn-icon" }),
        _hoisted_9
      ]),
      _: 1
    }),
    _createVNode(_component_Button, {
      class: "full-width-btn",
      onClick: _cache[4] || (_cache[4] = ($event: any) => (setBackgroundImage()))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_IconTheme, { class: "btn-icon" }),
        _hoisted_10
      ]),
      _: 1
    })
  ]))
}
}

})